<script setup lang="ts">
import type { SportsBookMenuItem } from '@/types/SportsBook';
import Sidebarv2MenuItem from '~/components/sidebarv2/MenuItem.vue';

interface Props {
  isLoggedUser?: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  onClickMenu: [];
  openRegisterModal: [];
}>();

const { isSidebarMenuOpen } = useLayout();

const {
  goToSportBookPage,
  championshipMenuItems,
  sportsMenuItems: allSportsMenuItems,
} = useSportsBook();

const desiredSportsSlugs: Set<string> = new Set([
  'brasil/brasileirao-serie-a',
  'basketball',
  'tennis',
  'formula-1',
]);

const filteredChampionshipItems = computed(() => {
  return championshipMenuItems.value.filter((item: SportsBookMenuItem) =>
    desiredSportsSlugs.has(item.slug),
  );
});

const filteredSportsItems = computed(() => {
  return allSportsMenuItems.value.filter((item: SportsBookMenuItem) =>
    desiredSportsSlugs.has(item.slug),
  );
});

const desiredOrder: string[] = [
  'brasil/brasileirao-serie-a',
  'basketball',
  'tennis',
  'formula-1',
];

const whiteIconSlugs: Set<string> = new Set(['formula-1']);

const sportsMenuItems = computed(() => {
  const championshipSlugs = new Set(
    filteredChampionshipItems.value.map(
      (item: SportsBookMenuItem) => item.slug,
    ),
  );

  const sportsOnly = filteredSportsItems.value.filter(
    (item: SportsBookMenuItem) => !championshipSlugs.has(item.slug),
  );

  const allItems = [...filteredChampionshipItems.value, ...sportsOnly];
  const itemsBySlug = new Map<string, SportsBookMenuItem>(
    allItems.map((item) => [item.slug, item]),
  );

  const orderedItems = desiredOrder
    .map((slug) => itemsBySlug.get(slug))
    .filter((item): item is SportsBookMenuItem => item !== undefined);

  return orderedItems.slice(0, 6);
});

const showNormalGrid = computed(() => isSidebarMenuOpen.value);
const showClosedContent = computed(() => !isSidebarMenuOpen.value);

const route = useRoute();

function isSportsButtonActive(btn: { link: string }) {
  if (btn.link === '/sports') {
    return (
      route.path === '/sports' &&
      (!route.query.tab ||
        route.query.tab === '' ||
        route.fullPath === '/sports')
    );
  }
  if (btn.link.startsWith('/sports?tab=Live')) {
    return route.path === '/sports' && route.query.tab === 'Live';
  }

  return false;
}

const sportsButtons = [
  {
    nome: 'Esportes',
    imgsrc: '/assets/images/menu-lateral-grid/esportes.png',
    color: '#1515E5',
    topImg: '-13px',
    rightImg: '0px',
    link: '/sports',
  },
  {
    nome: 'Ao vivo',
    imgsrc: '/assets/images/menu-lateral-grid/ao-vivo-flag.png',
    color: '#FF3400',
    topImg: '-13px',
    rightImg: '10px',
    link: '/sports?tab=Live',
  },
];

function getButtonGradient(color: string) {
  return `radial-gradient(168.3% 138% at 36.76% -16.67%, ${color} 0%, ${color}CC 100%)`;
}

function handleSportsButtonClick(link: string) {
  emit('onClickMenu');
  navigateTo(link);
}
</script>

<template>
  <!-- Versão normal dos botões (menu aberto) -->
  <template v-if="showNormalGrid">
    <button
      v-for="btn in sportsButtons"
      :key="btn.nome"
      class="flex h-[52px] items-center gap-[18px] px-[18px] py-0 rounded-[13px] border border-solid border-[rgba(255,255,255,0.10)] relative cursor-pointer select-none mx-[15px]"
      :style="{ background: getButtonGradient(btn.color) }"
      type="button"
      @click="handleSportsButtonClick(btn.link)"
    >
      <span class="text-sm font-semibold leading-4 text-white">{{
        btn.nome
      }}</span>
      <NuxtImg
        :alt="btn.nome"
        class="absolute object-cover h-[64px]"
        draggable="false"
        :src="getAssetsUrlForCDN(btn.imgsrc)"
        :style="{
          top: btn.topImg,
          right: btn.rightImg,
          position: 'absolute',
        }"
      />
    </button>
  </template>

  <!-- Versão compacta dos botões (menu fechado) -->
  <span v-if="showClosedContent"></span>
  <template v-if="showClosedContent">
    <button
      v-for="btn in sportsButtons"
      :key="btn.nome + '-small'"
      v-tooltip.desktopOnly="btn.nome"
      :class="[
        'flex h-[48px] w-[48px] items-center justify-center rounded-[12px] relative cursor-pointer select-none mx-auto overflow-hidden',
        { active: isSportsButtonActive(btn) },
      ]"
      :style="{
        background: isSportsButtonActive(btn)
          ? getButtonGradient(btn.color)
          : 'rgba(255, 255, 255, 0.05)',
      }"
      type="button"
      @click="handleSportsButtonClick(btn.link)"
    >
      <NuxtImg
        :alt="btn.nome"
        class="w-full"
        draggable="false"
        :src="getAssetsUrlForCDN(btn.imgsrc.replace('.png', '-small.png'))"
      />
    </button>
  </template>

  <div
    :class="[
      'sidebar-toggle flex flex-col justify-center items-stretch self-stretch',
      isSidebarMenuOpen
        ? 'gap-[5px] px-[15px]'
        : 'p-0 w-[70px] rounded-none mx-0 border-0',
    ]"
  >
    <div
      v-if="showNormalGrid"
      class="flex flex-col gap-[5px] w-full mb-4"
    >
      <template
        v-for="{ slug, i18nLabel, icon, isActive } in sportsMenuItems"
        :key="slug"
      >
        <Sidebarv2MenuItem
          v-tooltip.desktopOnly.closedSideBarOnly="$t(i18nLabel)"
          glow-color="#0606ad"
          :is-active="isActive"
          is-button
          @click="
            goToSportBookPage(slug);
            emit('onClickMenu');
          "
        >
          <NuxtIcon
            v-if="'name' in icon && icon.name"
            :class="['sidebarIcon', { whiteIcon: whiteIconSlugs.has(slug) }]"
            :filled="true"
            :name="icon.name"
          />
          <NuxtIcon
            v-else-if="'path' in icon && icon.path"
            :alt="$t(i18nLabel)"
            :class="['sidebarIcon', { whiteIcon: whiteIconSlugs.has(slug) }]"
            :filled="true"
            :name="icon.path"
          />
          <span>{{ $t(i18nLabel) }}</span>
        </Sidebarv2MenuItem>
      </template>
    </div>

    <template v-if="showClosedContent">
      <template
        v-for="{ slug, i18nLabel, icon, isActive } in sportsMenuItems"
        :key="slug"
      >
        <Sidebarv2MenuItem
          v-tooltip.desktopOnly.closedSideBarOnly="$t(i18nLabel)"
          glow-color="#0606ad"
          :is-active="isActive"
          is-button
          @click="
            goToSportBookPage(slug);
            emit('onClickMenu');
          "
        >
          <NuxtIcon
            v-if="'name' in icon && icon.name"
            :class="['sidebarIcon', { whiteIcon: whiteIconSlugs.has(slug) }]"
            :filled="true"
            :name="icon.name"
          />
          <NuxtIcon
            v-else-if="'path' in icon && icon.path"
            :alt="$t(i18nLabel)"
            :class="['sidebarIcon', { whiteIcon: whiteIconSlugs.has(slug) }]"
            :filled="true"
            :name="icon.path"
          />
          <span>{{ $t(i18nLabel) }}</span>
        </Sidebarv2MenuItem>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.sidebar-toggle {
  @apply border-solid border-b border-sidebar-links/10 select-none;
}
button.active {
  background: rgba(8, 86, 150, 0.719);
}

:deep(.whiteIcon) {
  filter: brightness(0) invert(1);
}

:deep([class*='isClosed'] .whiteIcon) {
  filter: brightness(0);
}

:deep([class*='isActive'] .whiteIcon) {
  filter: none;
}

@media (hover: hover) {
  :deep([class*='menuLink']:hover .whiteIcon) {
    filter: none;
  }
}
</style>
